import { PropTypes } from "prop-types";
import React from "react";
import autoBind from "react-autobind";
import { Popover } from "react-bootstrap";
import LeadDrawerActions from "~/actions/lead_drawer_actions";
import SharedEmailForm from "~/components/forms/EmailForm/email_form";
import UnsupportedVariablesWarning from "~/components/modals/UnsupportedVariablesWarning";
import { WarningMessage } from "~/components/warnings/GooglePermissionDeniedWarning";
import {
  CC_BCC_SEND_MODES,
  captureCcBccUsedEvent,
} from "~/helpers/capture_cc_bcc_used_event";
import { checkFeatureFlag } from "~/helpers/FeatureFlagChecker";
import { normalizeUnsupportedVariablesError } from "~/lib/unsupportedVariables";
import LeadDrawerStore from "~/stores/lead_drawer_store";
import FormFooter from "./common/form_footer";

class EmailForm extends React.Component {
  static getStores() {
    return [LeadDrawerStore];
  }

  static calculateState() {
    return {
      leadDrawerState: LeadDrawerStore.getState(),
    };
  }

  captureCopyRecipientAnalytics = () => {
    const { analyticsLocation } = this.props;
    const { email } = this.state;

    if (email.id) {
      return;
    }

    captureCcBccUsedEvent({
      location: analyticsLocation,
      sendMode: CC_BCC_SEND_MODES.PER_MESSAGE,
      ccRecipients: email.cc_recipients,
      bccRecipients: email.bcc_recipients,
    });
  };

  constructor(props) {
    super(props);

    const { lead, email, schedule } = this.props;
    const { currentUser } = Rails.helpers;

    this.state = {
      leadDrawerState: LeadDrawerStore.getState(),
      email: {
        id: email?.id || null,
        subject: email?.subject || "",
        body: email?.body || "",
        cc_recipients: email?.cc_recipients || [],
        bcc_recipients: email?.bcc_recipients || [],
        attachment_ids: email?.attachment_ids || "",
        attachments: email?.attachments || [],
        references: email?.references || [],
      },
      status: lead.status,
      schedule: schedule || {
        customOptions: { timezone: currentUser.timezone },
      },
      sending: false,
      showPopover: false,
      warningMessage: "",
      variablesWarning: null,
      submittingSaveAnyway: false,
    };

    autoBind(this);
  }

  componentDidMount() {
    this.leadStoreListener = LeadDrawerStore.addListener(
      this.onLeadStoreChange,
    );
    const { email, schedule } = this.props;

    this.setState((prevState) => ({
      email: { ...prevState.email, ...email },
      schedule: { ...prevState.schedule, ...schedule },
      scheduleEmailsAllowed: checkFeatureFlag(
        process.env.SHAKAPACKER_PUBLIC_ALLOW_SCHEDULE_EMAILS_FLAG,
      ),
    }));
  }

  componentDidUpdate(prevProps) {
    const { email, schedule } = this.props;
    if (schedule !== prevProps.schedule || email?.id !== prevProps.email?.id) {
      this.setState((prevState) => ({
        email: { ...prevState.email, ...email },
        schedule,
      }));
    }
  }

  componentWillUnmount() {
    if (this.leadStoreListener) {
      this.leadStoreListener.remove();
    }

    this.setState({
      email: {
        id: null,
        subject: "",
        body: "",
        cc_recipients: [],
        bcc_recipients: [],
        attachment_ids: "",
        references: [],
      },
      status: "",
      schedule: {},
    });
  }

  variableWarningSubmitOptions = () => ({
    onUnsupportedVariables: (warning) => {
      this.footer?.resetSending?.();
      this.setState({
        variablesWarning: normalizeUnsupportedVariablesError(warning),
        submittingSaveAnyway: false,
        sending: false,
      });
    },
    onDone: () => {
      this.captureCopyRecipientAnalytics();
      this.setState({
        variablesWarning: null,
        submittingSaveAnyway: false,
        sending: false,
      });
    },
    onFail: () => {
      this.footer?.resetSending?.();
      this.setState({
        variablesWarning: null,
        submittingSaveAnyway: false,
        sending: false,
      });
    },
  });

  onFormSubmit = (e, saveAnyway = false) => {
    e.preventDefault();

    const { lead } = this.props;
    const { schedule, email } = this.state;

    this.setState({ sending: true });

    const errors = this.validate();
    if (Object.keys(errors).length === 0) {
      const _footer = this.footer.serialize();
      const _attrs = {
        ..._footer,
        email: {
          ...email,
          internal: _footer.internal,
          schedule,
        },
        save_anyway: saveAnyway,
      };

      if (email?.id) {
        LeadDrawerActions.updateLeadEmail(
          lead,
          email.id,
          _attrs,
          this.variableWarningSubmitOptions(),
        );
      } else {
        LeadDrawerActions.createLeadEmail(
          lead,
          _attrs,
          this.variableWarningSubmitOptions(),
        )
          .catch((error) => {
            const errorMsg =
              error.response?.body?.errors ||
              error.response?.body?.error ||
              "An error occurred, please try again later.";

            const isPermissionError =
              errorMsg.toLowerCase().includes("permission") ||
              errorMsg.toLowerCase().includes("scope") ||
              error.response?.body?.hasRequiredScope === false;

            if (isPermissionError) {
              this.setState({
                warningMessage: errorMsg,
                showPopover: true,
              });
            } else {
              GlobalContainer.notify(errorMsg, "error");
              this.setState({ sending: false });
            }
          })
          .finally(() => {
            this.setState({ sending: false });
          });
      }
    } else {
      this.setState({ sending: false });
    }
  };

  handlePopoverClose = () => {
    this.setState({
      showPopover: false,
      sending: false,
      warningMessage: "",
    });
  };

  renderPopover = (props) => {
    const { warningMessage } = this.state;

    return (
      <Popover id="dropdown-message-id" {...props}>
        <Popover.Content className="bg-grey-lightest">
          <WarningMessage
            message={warningMessage}
            onClose={this.handlePopoverClose}
          />
        </Popover.Content>
      </Popover>
    );
  };

  onLeadStoreChange = () => {
    const leadDrawerState = LeadDrawerStore.getState();
    this.setState({ leadDrawerState });
    const { leadDrawerStoreAction } = leadDrawerState;

    if (
      leadDrawerStoreAction === "createLeadEmailFail" ||
      leadDrawerStoreAction === "updateLeadEmailFail"
    ) {
      this.setState({ sending: false });
    }
  };

  setSchedule = (newSchedule) => {
    this.setState((prevState) => ({
      schedule: { ...prevState.schedule, ...newSchedule },
    }));
  };

  assignEmailState = (newEmail) => {
    this.setState((prevState) => ({
      email: { ...prevState.email, ...newEmail },
    }));
  };

  validate() {
    return this.footer.validate(this.emailForm.validate());
  }

  render() {
    const {
      status,
      internal,
      schedule,
      email,
      scheduleEmailsAllowed,
      sending,
      showPopover,
      warningMessage,
      variablesWarning,
      submittingSaveAnyway,
    } = this.state;
    const { errors, lead, scheduleBtnPopoverPlacement } = this.props;
    const { currentUser } = Rails.helpers;
    const {
      subject,
      body,
      cc_recipients,
      bcc_recipients,
      attachments,
      references,
    } = email;

    let submitButtonText = "Send";

    if (currentUser.is_microsoft) {
      submitButtonText = "Send with Microsoft";
    } else if (currentUser.is_google) {
      submitButtonText = "Send with Google";
    }

    return (
      <>
        <form onSubmit={this.onFormSubmit}>
          <SharedEmailForm
            ref={(el) => (this.emailForm = el)}
            onChange={this.assignEmailState}
            errors={errors}
            enableCCField
            enableBCCField
            subject={subject}
            body={body}
            cc_recipients={cc_recipients}
            bcc_recipients={bcc_recipients}
            attachments={attachments}
            references={references}
            leadId={lead.id}
          />

          <div className="mt15 mb15">
            <FormFooter
              ref={(el) => (this.footer = el)}
              status={status}
              activityType="direct_email"
              internal={internal}
              lead={lead}
              submitButtonText={submitButtonText}
              onSubmit={this.onFormSubmit}
              showScheduleButton={scheduleEmailsAllowed}
              schedule={schedule}
              setSchedule={this.setSchedule}
              sending={sending}
              scheduleBtnPopoverPlacement={scheduleBtnPopoverPlacement}
              isEditingMode={!!email.id}
              showPopover={showPopover}
              renderPopover={this.renderPopover}
              warningMessage={warningMessage}
            />
          </div>
        </form>
        <UnsupportedVariablesWarning
          show={!!variablesWarning}
          warning={variablesWarning}
          saving={submittingSaveAnyway}
          onReview={() => this.setState({ variablesWarning: null })}
          onSaveAnyway={() => {
            this.setState({ submittingSaveAnyway: true, sending: true });
            this.onFormSubmit({ preventDefault: () => false }, true);
          }}
        />
      </>
    );
  }
}

EmailForm.defaultProps = {
  lead: {},
  scheduleBtnPopoverPlacement: "bottom",
  analyticsLocation: "direct",
};

EmailForm.propTypes = {
  lead: PropTypes.shape({}),
  scheduleBtnPopoverPlacement: PropTypes.string,
  analyticsLocation: PropTypes.string,
};

export default EmailForm;
